import type { AcceptResult, AccountPremium, Change, Invite, PullResult, PushResult, RemoteRecord, SyncTransport, SyncedTable } from './types';

/**
 * The server functions (supabase/migrations) as a SyncTransport. `call`
 * runs one function with named arguments: over HTTP in the app
 * (supabase-js rpc), on an in-process Postgres in tests. A set-returning
 * function answers rows; the others answer their value.
 */
export type RpcCall = (fn: string, args: Record<string, unknown>, returnsSet: boolean) => Promise<unknown>;

type PullRow = { tbl: string; id: string; space_id: string; data: Record<string, unknown>; field_hlc: Record<string, string>; seq: number | string };

export function rpcTransport(call: RpcCall): SyncTransport {
  const one = <T>(fn: string, args: Record<string, unknown> = {}) => call(fn, args, false) as Promise<T>;
  const rows = async <T>(fn: string, args: Record<string, unknown> = {}) => ((await call(fn, args, true)) ?? []) as T[];
  return {
    claim: (localUserId) => one<string>('sync_claim', { local_user_id: localUserId }),
    push: (changes: readonly Change[]) => one<PushResult>('sync_push', { changes }),
    async pull(cursor: number, limit = 500, spaceId?: string): Promise<PullResult> {
      const page = await rows<PullRow>('sync_pull', { after_seq: cursor, max_rows: limit, ...(spaceId ? { only_space: spaceId } : {}) });
      const records: RemoteRecord[] = page.map((r) => ({
        table: r.tbl as SyncedTable,
        id: r.id,
        spaceId: r.space_id,
        fields: r.data,
        fieldHlc: r.field_hlc,
        seq: Number(r.seq),
      }));
      return { records, cursor: records.length ? records[records.length - 1]!.seq : cursor, hasMore: records.length === limit };
    },
    async access() {
      return (await rows<{ space_id: string; role: 'owner' | 'member'; premium?: boolean | null }>('sync_access')).map((r) => ({
        spaceId: r.space_id,
        role: r.role,
        // A server from before Premium does not say: nothing is held for it.
        premium: r.premium ?? true,
      }));
    },
    createInvite: (spaceId) => one<Invite>('sync_invite_create', { space: spaceId }),
    listInvites: async (spaceId) => (await one<Invite[] | null>('sync_invites', { space: spaceId })) ?? [],
    async revokeInvite(code) {
      await one('sync_invite_revoke', { invite_code: code });
    },
    acceptInvite: (code) => one<AcceptResult>('sync_invite_accept', { invite_code: code }),
    premium: () => one<AccountPremium>('sync_premium_status'),
    async removeMember(spaceId, userId) {
      await one('sync_member_remove', { space: spaceId, member_user_id: userId });
    },
    async deleteAccount() {
      await one('sync_account_delete');
    },
    async leaveSpace(spaceId) {
      await one('sync_space_leave', { space: spaceId });
    },
  };
}
